import * as React from 'react';
import { Slot as SlotPrimitive } from 'radix-ui';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

/**
 * The agents' black primary button (`LibraryButton` variant `dark` in
 * containers/agents/library-toolbar): a charcoal gradient, a 1px border, a
 * light inset top edge and white text; inverted in dark mode. The fill alone,
 * for a surface that holds more than one button (a split button). A candy
 * icon's outline takes the text colour, so it shows on the fill. Its coloured
 * parts don't, so a chevron on the fill takes `mono` (candy's purple chevron
 * vanishes on the charcoal).
 */
export const PRIMARY_BUTTON_FILL =
  '[--cl-base:currentColor] border border-[#262626] bg-[linear-gradient(180deg,#434343_0%,#262626_100%)] text-white shadow-[inset_0_1.5px_0_0_rgba(255,255,255,0.24)] dark:border-[#e5e5e5] dark:bg-[linear-gradient(180deg,#ffffff_0%,#e4e4e4_100%)] dark:text-[#171717] dark:shadow-[inset_0_1.5px_0_0_rgba(255,255,255,0.6)]';

/**
 * The black primary button's classes, for a primary action that renders its
 * own element (a link, a native button) instead of a Button.
 */
export const PRIMARY_BUTTON = cn(
  'rounded-[8px] transition-[opacity,background-color] hover:opacity-90',
  PRIMARY_BUTTON_FILL
);

const buttonVariants = cva(
  // Flat pill buttons: rounded-full + semibold labels, matching the marketing
  // site's flat pill CTAs. The tonal variants below carry no shadow to stay flat;
  // the sm/lg sizes inherit the pill radius (no per-size radius override).
  'inline-flex items-center justify-center gap-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // The black primary button. Its 8px radius is important, as the app's
        // global `button.bg-primary` rule made it for the old blue default: a
        // caller's rounded-* class doesn't change it, a `!rounded-*` does.
        default: cn(
          '!rounded-[8px] transition-[opacity,background-color] hover:opacity-90',
          PRIMARY_BUTTON_FILL
        ),
        // The brand blue, kept for the composer's send arrow and a selected
        // state (the chosen option, the current page). Recolour it with a bg
        // class; a primary action is the default.
        brand: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90 dark:bg-red-700 dark:hover:bg-red-700/90',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 px-3 text-xs',
        lg: 'h-10 px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? SlotPrimitive.Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
